import React, { FC, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import { ListItemIcon, ListItemText, Typography } from "@mui/material";
import DateFilterIcon from "../../../assets/svg-fc/DateFilterIcon";
import IsMobile from "../../../hooks/isMobile";
import typography from "../../../theme/typography";
import palette from "../../../theme/palette";
import TickIcon from "../../../assets/svg-fc/TickIcon";
import { DateRange, Range } from "react-date-range";
import { enUS } from "date-fns/locale";
import { IDateFilter, ISelectedDateRangeFilter } from "../../../types/commonTypes";
import { DateFilterEnum } from "../../../constants/enums";
import { DATE_FILTERS } from "../../../constants/commonConstants";
import { DEFAULT_DATE_FORMAT, DEFAULT_DATE_FILTER_FORMAT, fDate } from "../../../utils/formatTime";
import CrossIconSvg from "../../../assets/svg-fc/CrossIconSvg";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./dateRangeFilter.scss";

interface IHeaderDateFilter {
	selectedFilter: ISelectedDateRangeFilter;
	setSelectedFilter: (filter: ISelectedDateRangeFilter) => void;
}

const HeaderDateFilter: FC<IHeaderDateFilter> = ({ selectedFilter, setSelectedFilter }) => {
	const isMobile = IsMobile();
	const [dateMenuAnchor, setDateMenuAnchor] = React.useState<null | HTMLElement>(null);
	const [selectedDateFilter, setSelectedDateFilter] = useState<DateFilterEnum>(
		selectedFilter.selectedDateFilter,
	);
	const [selectionRange, setSelectionRange] = useState<Range[]>([
		{
			startDate: new Date(selectedFilter.startDate),
			endDate: new Date(selectedFilter.startDate),
			key: "selection",
		},
	]);
	const [isCalendarOpen, setCalendarOpen] = useState(false);
	const isDateMenuOpen = Boolean(dateMenuAnchor);

	const handleDateMenuClick = (event: React.MouseEvent<HTMLElement>) => {
		setDateMenuAnchor(event.currentTarget);
	};

	const handleDateMenuClose = () => {
		setDateMenuAnchor(null);
	};

	const onChangeSelectedDateFilter = (id: DateFilterEnum, startDate: Date, endDate: Date) => {
		setSelectedDateFilter(id);
		setSelectedFilter({
			selectedDateFilter: id,
			startDate: fDate(startDate, DEFAULT_DATE_FILTER_FORMAT),
			endDate: fDate(endDate, DEFAULT_DATE_FILTER_FORMAT),
		});
	};

	useEffect(() => {
		if (selectedFilter.selectedDateFilter !== DateFilterEnum.CUSTOM) {
			const dateFilter = DATE_FILTERS.find(
				(filter) => filter.id === selectedFilter.selectedDateFilter,
			) as IDateFilter;
			setSelectedFilter({
				...selectedFilter,
				startDate: fDate(dateFilter?.startDate, DEFAULT_DATE_FILTER_FORMAT),
				endDate: fDate(dateFilter?.endDate, DEFAULT_DATE_FILTER_FORMAT),
			});
		}
	}, []);

	return (
		<>
			<Button
				className="filterButton"
				onClick={handleDateMenuClick}
				aria-controls={isDateMenuOpen ? "date-menu" : undefined}
				aria-haspopup="true"
				aria-expanded={isDateMenuOpen ? "true" : undefined}
			>
				<DateFilterIcon size={isMobile ? "18" : "24"} />
				{!isMobile && (
					<Typography className="filterButtonLabel">
						{selectedDateFilter === DateFilterEnum.CUSTOM
							? `${fDate(selectionRange[0]?.startDate as Date, DEFAULT_DATE_FORMAT)} - ${fDate(selectionRange[0]?.endDate as Date, DEFAULT_DATE_FORMAT)}`
							: DATE_FILTERS.find((filter) => filter.id === selectedDateFilter)?.label}
					</Typography>
				)}
			</Button>
			<Menu
				id="date-menu"
				anchorEl={dateMenuAnchor}
				open={isDateMenuOpen}
				onClose={handleDateMenuClose}
				hideBackdrop
				onClickCapture={handleDateMenuClose}
				sx={{
					"& .MuiPaper-root": {
						marginLeft: isMobile ? "-48px" : "-60px",
						marginTop: "12px",
					},
					"& .MuiList-padding": {
						padding: 0,
					},
				}}
				slotProps={{
					list: {
						"aria-labelledby": "date-button",
					}
				}}
			>
				{DATE_FILTERS.map(({ id, label, startDate, endDate }) => {
					const isSelected = selectedDateFilter === id;
					return (
						<MenuItem
							sx={{
								fontFamily: typography.fontFamily,
								fontWeight: typography.fontWeightMedium,
								color: palette.secondary.main,
								fontSize: "16px",
								padding: "12px 16px",
								minWidth: "195px",
								backgroundColor: isSelected ? "#F6F6F6" : "transparent",
							}}
							key={id}
							value={label}
							onClick={() => {
								if (id === DateFilterEnum.CUSTOM) {
									setSelectionRange([
										{
											startDate:
												selectedDateFilter === DateFilterEnum.CUSTOM
													? new Date(selectedFilter?.startDate)
													: startDate,
											endDate:
												selectedDateFilter === DateFilterEnum.CUSTOM
													? new Date(selectedFilter?.endDate)
													: endDate,
											key: "selection",
										},
									]);
									setCalendarOpen(true);
								} else {
									onChangeSelectedDateFilter(id, startDate, endDate);
								}

								handleDateMenuClose();
							}}
						>
							<ListItemText primary={label} />
							{isSelected && (
								<ListItemIcon sx={{ justifyContent: "flex-end" }}>
									<TickIcon />
								</ListItemIcon>
							)}
						</MenuItem>
					);
				})}
			</Menu>
			{isCalendarOpen && (
				<Modal
					open={isCalendarOpen}
					onClose={() => setCalendarOpen(false)}
					aria-labelledby="date-range-modal"
					className="dateRangeModal"
				>
					<Box className="dateRangePickerContainer">
						<Box className="dateRangePickerHeader">
							<Typography className="dateRangePickerLabel">Custom Range</Typography>
							<Box className="crossIconContainer" onClick={() => setCalendarOpen(false)}>
								<CrossIconSvg crosIconHandler={() => setCalendarOpen(false)} size="12" />
							</Box>
						</Box>
						<DateRange
							editableDateInputs={false}
							onChange={(item) => setSelectionRange([item.selection])}
							moveRangeOnFirstSelection={false}
							showMonthAndYearPickers={false}
							ranges={selectionRange}
							locale={enUS}
							maxDate={new Date()}
							color={palette.primary.main}
							rangeColors={[palette.primary.main, palette.primary.main]}
							retainEndDateOnFirstSelection={false}
						/>
						<Button
							className="dateRangePickerButton"
							onClick={() => {
								onChangeSelectedDateFilter(
									DateFilterEnum.CUSTOM,
									selectionRange[0]?.startDate as Date,
									selectionRange[0]?.endDate as Date,
								);
								setCalendarOpen(false);
							}}
						>
							Show Results
						</Button>
					</Box>
				</Modal>
			)}
		</>
	);
};

export default HeaderDateFilter;
